import { CommonModule } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    Output,
    ViewChild,
} from "@angular/core";
import { Observable, Subscription } from "rxjs";

import { LayoutFacade } from "@hermes/aphrodite/layout";
import {
    MediaPictureComponent,
    MediaQueryOption,
} from "@hermes/aphrodite/media-picture";
import { VideoComponent } from "@hermes/aphrodite/video";
import { CTALink, CTAToken, Media } from "@hermes/api-model-editorial";
import { LOCALE } from "@hermes/app-core";

import {
    EditoAnalyticsService,
    transformSPALink,
    EditorialCTAResolverModule,
} from "@hermes/fragments/editorial-cta-resolver";

import { Locale } from "@hermes/locale";
import {
    RouterService,
    RouterServiceModule,
} from "@hermes/utils-generic/services/router";

import {
    displayAriaLabel,
    getMediaName,
    resolveCTA,
    setMediaImageAltFromCTA,
    showFigureCaption,
} from "../../helpers/media-cta.helpers";
import { MediaCaptionsComponent } from "../media-captions/media-captions.component";

@Component({
    selector: "h-media-cta",
    templateUrl: "./media-cta.component.html",
    styleUrls: ["./media-cta.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        VideoComponent,
        EditorialCTAResolverModule,
        MediaPictureComponent,
        MediaCaptionsComponent,
        RouterServiceModule,
    ],
})
export class MediaCtaComponent implements OnDestroy {
    @Input()
    public mediaOptions?: MediaQueryOption[];

    @Input()
    public preloadPoster = false;

    @Input()
    public showSoundControlButton = false;

    @Input()
    public isLightMode = false;

    // Used to pass to video component if we need to display a black gradient a the bottom of the video
    // (to display white text above the video)
    @Input()
    public displayGradient: boolean = false;

    @Input()
    public videoRatio?: `${number}-${number}`;

    // Fill mode for videojs
    @Input()
    public isFillMode?: boolean = false;

    /**
     * Determines whether this component sends analytics events upon click
     * (i.e., when calling `mediaRedirection`).
     *
     * If set to `false`, analytics will only be sent from parent components,
     * and not from this component itself.
     *
     * Defaults to `true`.
     */
    @Input()
    public shouldSendDefaultRedirectionAnalytics: boolean = true;

    @Output() public mediaRedirectionEvent = new EventEmitter<CTALink>();

    @ViewChild(VideoComponent)
    public videoComponent: VideoComponent | undefined;

    public isKeyboardNavigation$ = this.layoutFacade.keyboardNavigation$;

    public subscription: Subscription = new Subscription();

    public cta?: CTALink;
    public mediaName!: string;
    public showFigureCaption!: boolean;
    public videoUrl?: string;
    public mobileVideoUrl?: string;
    public ariaLabel?: string;
    public linkAriaLabel?: string;

    private prMedia!: Media;
    private prTitle?: string;

    constructor(
        @Inject(LOCALE) private locale: Locale,
        private layoutFacade: LayoutFacade,
        private editoAnalyticsService: EditoAnalyticsService,
        private routerService: RouterService,
    ) {}

    public get media(): Media | undefined {
        return this.prMedia;
    }

    @Input({ required: true })
    public set media(media: Media) {
        this.mediaName = getMediaName(media);
        this.showFigureCaption = showFigureCaption(media);
        this.videoUrl = media.videoUrl;
        this.mobileVideoUrl = media.mobileVideoUrl;
        if (media.token) {
            this.cta = resolveCTA(media.token);
        }
        this.prMedia = setMediaImageAltFromCTA(media, this.cta);
        this.ariaLabel = displayAriaLabel(this.prMedia, this.prTitle);
    }

    @Input()
    public set token(token: CTAToken | undefined) {
        this.cta = resolveCTA(token);
        if (this.prMedia) {
            this.prMedia = setMediaImageAltFromCTA(this.prMedia, this.cta);
        }
    }

    @Input() public set pauseEvent$(pause$: Observable<void>) {
        this.setPauseObservable(pause$);
    }

    @Input() public set title(mediaTitle: string | undefined) {
        this.prTitle = mediaTitle;
        if (mediaTitle) {
            this.linkAriaLabel = $localize`:@@hermes.category_push.aria_label:Voir la catégorie ${mediaTitle}:INTERPOLATION:`;
        }
    }

    public get title(): string | undefined {
        return this.prTitle;
    }

    public setPauseObservable(pause$: Observable<void>) {
        this.subscription.add(
            pause$.subscribe(() => {
                this.pauseVideo();
            }),
        );
    }

    public mediaRedirection(event: MouseEvent, cta?: CTALink): void {
        event.preventDefault();
        event.stopPropagation();
        const targetUrl = cta?.path || this.cta?.path;
        if (!targetUrl) {
            return;
        }
        // Send analytics and emit event before triggering navigation
        if (this.shouldSendDefaultRedirectionAnalytics) {
            this.editoAnalyticsService.sendCTAClickEvent(targetUrl);
        }

        this.mediaRedirectionEvent.emit(cta);

        const spaUrl = transformSPALink(this.locale, targetUrl);
        this.routerService.spaRedirect(spaUrl);
    }

    public pauseVideo() {
        if (this.videoComponent) {
            this.videoComponent.autoPauseVideo();
        }
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}
